import React, { useEffect, useState } from "react";
import clsx from "clsx";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useLazyQuery, useQuery } from "@apollo/client";
import {
  ACTIVITY_PILLAR_DETAIL_V2,
  ENERGY_PILLAR_DETAIL_V2,
  LEARNER_SCREEN_INDEX_GRAPH_MONTH_ACTIVITY,
  LEARNER_SCREEN_INDEX_GRAPH_MONTH_ENERGY,
  LEARNER_SCREEN_INDEX_GRAPH_MONTH_RESILIENCE,
  LEARNER_SCREEN_INDEX_GRAPH_WEEK_ACTIVITY,
  LEARNER_SCREEN_INDEX_GRAPH_WEEK_ENERGY,
  LEARNER_SCREEN_INDEX_GRAPH_WEEK_RESILIENCE,
  LEARNER_SCREEN_INDEX_GRAPH_YEAR_ACTIVITY,
  LEARNER_SCREEN_INDEX_GRAPH_YEAR_ENERGY,
  LEARNER_SCREEN_INDEX_GRAPH_YEAR_RESILIENCE,
  LEARNER_SCREEN_INDEX_V1,
  LEARNER_SCREEN_INDEX_V2,
  RESILIENCE_PILLAR_DETAIL_V2,
} from "utility/graphQl/query";
import appInfo from "utility/app-info";
import IndexScore from "apps/user/common/components/index-score";

import IndexScoreIcon from "Assets/icon/iconIndexScore.svg";
import SelfAssessmentScoreIcon from "Assets/icon/self-assessment-score.svg";
import BiometricsIcon from "Assets/icon/biometrics.svg";
import SessionIcon from "Assets/icon/iconSessionBlack.svg";
import RecordingIcon from "Assets/icon/iconVideoBlack.svg";
import ArticleIcon from "Assets/icon/iconArticleBlack.svg";

import styles from "./index.module.scss";

import {
  PageContainer,
  PageHeader,
  PageContent,
  PageLoading,
  PageError,
} from "apps/user/learner/components/page-container";
import ContentListPreview from "apps/user/learner/components/content-list/preview";

import PillarDetailsContentList from "./pillar-details-content-list";
import { LearnerIndexBreakdownPillar } from "../../api/types";
import IndexScoreContainer from "./components/index-score-container";
import { Nav, NavItem, NavLink } from "reactstrap";
import IndexScoreGraph from "./components/index-score-graph";
import { useAuth } from "providers/auth";

import { logError } from "utility/Function";
import { useTranslation } from "lib/i18n/use-translation";

const PillarDetailsContentV1 = ({ breakdownPillar }) => {
  return (
    <>
      <div className={styles["index-score-sec"]}>
        {breakdownPillar?.score?.labelHtml && (
          <span
            className={styles["index-score-desc"]}
            dangerouslySetInnerHTML={{
              __html: breakdownPillar?.score?.labelHtml,
            }}
          />
        )}
        {breakdownPillar && (
          <IndexScore indexScoreData={breakdownPillar?.score} size={"big"} />
        )}
      </div>
      <div className={styles["wone-index-details"]}>
        <h2>{breakdownPillar?.pillarLabel}</h2>
        <p>{breakdownPillar?.descriptionHtml}</p>
      </div>
      <PillarDetailsContentList contentLists={breakdownPillar?.contentLists} />
    </>
  );
};

interface PillarDetailsContentV2Props {
  breakdownPillar: LearnerIndexBreakdownPillar;
  showBetaFlag: Boolean;
  fetchMore: any;
  loading: boolean;
}

const PillarDetailsContentV2 = ({
  breakdownPillar,
  showBetaFlag,
  fetchMore,
  loading,
}: PillarDetailsContentV2Props) => {
  const { t } = useTranslation("index");
  const [activeTab, setActiveTab] = useState<"plan" | "data">("plan");

  const [weekPeriodIndex, setWeekPeriodIndex] = useState(0);
  const [monthPeriodIndex, setMonthPeriodIndex] = useState(0);
  const [yearPeriodIndex, setYearPeriodIndex] = useState(0);

  const [periodLength, setPeriodLength] = useState<"week" | "month" | "year">(
    "week"
  );
  const { woneindexType } = useParams();
  let periodIndex, periodQuery;
  if (woneindexType === "energy") {
    if (periodLength === "week") {
      periodIndex = weekPeriodIndex;
      periodQuery = LEARNER_SCREEN_INDEX_GRAPH_WEEK_ENERGY;
    } else if (periodLength === "month") {
      periodIndex = monthPeriodIndex;
      periodQuery = LEARNER_SCREEN_INDEX_GRAPH_MONTH_ENERGY;
    } else if (periodLength === "year") {
      periodIndex = yearPeriodIndex;
      periodQuery = LEARNER_SCREEN_INDEX_GRAPH_YEAR_ENERGY;
    } else {
      throw new Error(`Unexpected periodLength value: ${periodLength}`);
    }
  } else if (woneindexType === "activity") {
    if (periodLength === "week") {
      periodIndex = weekPeriodIndex;
      periodQuery = LEARNER_SCREEN_INDEX_GRAPH_WEEK_ACTIVITY;
    } else if (periodLength === "month") {
      periodIndex = monthPeriodIndex;
      periodQuery = LEARNER_SCREEN_INDEX_GRAPH_MONTH_ACTIVITY;
    } else if (periodLength === "year") {
      periodIndex = yearPeriodIndex;
      periodQuery = LEARNER_SCREEN_INDEX_GRAPH_YEAR_ACTIVITY;
    } else {
      throw new Error(`Unexpected periodLength value: ${periodLength}`);
    }
  } else if (woneindexType === "resilience") {
    if (periodLength === "week") {
      periodIndex = weekPeriodIndex;
      periodQuery = LEARNER_SCREEN_INDEX_GRAPH_WEEK_RESILIENCE;
    } else if (periodLength === "month") {
      periodIndex = monthPeriodIndex;
      periodQuery = LEARNER_SCREEN_INDEX_GRAPH_MONTH_RESILIENCE;
    } else if (periodLength === "year") {
      periodIndex = yearPeriodIndex;
      periodQuery = LEARNER_SCREEN_INDEX_GRAPH_YEAR_RESILIENCE;
    } else {
      throw new Error(`Unexpected periodLength value: ${periodLength}`);
    }
  } else {
    throw new Error(`Unexpected pillar value: ${woneindexType}`);
  }

  const {
    pillarLabel,
    score,
    plan: { contentLists, descriptionHtml },
  } = breakdownPillar;

  const previousPeriodIndex = periodIndex + 1;
  const currentPeriodsLength = score?.graph.periods[periodLength]?.length;
  const shouldFetchMore = previousPeriodIndex >= currentPeriodsLength;

  const handleClickPreviousPeriod = async () => {
    if (shouldFetchMore) {
      await fetchMore({
        query: periodQuery,
        notifyOnNetworkStatusChange: true,
        fetchPolicy: "cache-first",
        variables: {
          appInfo,
          first: 1,
          offset: currentPeriodsLength,
        },
      });
    }

    if (periodLength === "week") {
      setWeekPeriodIndex((prev) => prev + 1);
    } else if (periodLength === "month") {
      setMonthPeriodIndex((prev) => prev + 1);
    } else if (periodLength === "year") {
      setYearPeriodIndex((prev) => prev + 1);
    }
  };

  const handleClickNextPeriod = () => {
    if (periodLength === "week") {
      if (weekPeriodIndex > 0) {
        setWeekPeriodIndex((prev) => prev - 1);
      }
    } else if (periodLength === "month") {
      if (monthPeriodIndex > 0) {
        setMonthPeriodIndex((prev) => prev - 1);
      }
    } else if (periodLength === "year") {
      if (yearPeriodIndex > 0) {
        setYearPeriodIndex((prev) => prev - 1);
      }
    }
  };

  const handleChangePeriodLength = (value: "week" | "month" | "year") => {
    setPeriodLength(value);
  };

  const toggle = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  return (
    <div className={styles["wone-index-pillar-details-content"]}>
      {showBetaFlag === true && (
        <p className={styles["beta-card"]}>{t("index:badge.beta")}</p>
      )}
      <IndexScoreContainer data={score} pillarLabel={pillarLabel} />
      <div
        dangerouslySetInnerHTML={{
          __html: score?.descriptionHtml,
        }}
      ></div>
      <Nav tabs>
        <NavItem className={styles["nav-item"]}>
          <NavLink
            active={activeTab === "plan"}
            className={clsx(
              styles["nav-link"],
              activeTab === "plan" && styles["nav-link-active"]
            )}
            onClick={() => {
              toggle("plan");
            }}
          >
            {t("index:pillarTabs.myPlan")}
          </NavLink>
        </NavItem>
        <NavItem className={styles["nav-item"]}>
          <NavLink
            active={activeTab === "data"}
            className={clsx(
              styles["nav-link"],
              activeTab === "data" && styles["nav-link-active"]
            )}
            onClick={() => {
              toggle("data");
            }}
          >
            {t("index:pillarTabs.myScores")}
          </NavLink>
        </NavItem>
      </Nav>
      {activeTab === "plan" && (
        <>
          {descriptionHtml && (
            <div className="mt-3">
              <p
                className={styles["description"]}
                dangerouslySetInnerHTML={{
                  __html: descriptionHtml,
                }}
              />
            </div>
          )}
          <div className="mt-3">
            {contentLists.map((list) => {
              let icon = null;
              if (list.__typename === "LearnerGroupSessionsList") {
                icon = SessionIcon;
              } else if (list.__typename === "LearnerRecordingsList") {
                icon = RecordingIcon;
              } else if (list.__typename === "LearnerArticlesList") {
                icon = ArticleIcon;
              }

              return (
                <ContentListPreview
                  icon={icon}
                  key={list.id}
                  data={list}
                  limit={3}
                />
              );
            })}
          </div>
        </>
      )}
      {activeTab === "data" && (
        <div className="mt-3 row">
          {score && (
            <>
              <div
                className={clsx(
                  styles["chart-container"],
                  "mt-4 col-12 col-xxl-4"
                )}
              >
                <h3 className={styles["title"]}>
                  <img src={IndexScoreIcon} alt="Biometrics icon" />{" "}
                  {score.graph.scoreTitle}
                </h3>
                <p className={styles["description"]}>
                  {score.graph.scoreSubtitle}
                </p>
                <IndexScoreGraph
                  scoreChangeIconColor="blue"
                  view="compact"
                  dataPeriods={score.graph.periods}
                  periodLength={periodLength}
                  onChangePeriodLength={handleChangePeriodLength}
                  periodIndex={periodIndex}
                  onClickNextPeriod={handleClickNextPeriod}
                  onClickPreviousPeriod={handleClickPreviousPeriod}
                  assessmentColor="#1058e5"
                  biometricsColor="#a3d0e3"
                  moreLoading={shouldFetchMore && loading}
                />
              </div>
              <div
                className={clsx(
                  styles["chart-container"],
                  "mt-4 col-12  col-xxl-4"
                )}
              >
                <h3 className={styles["title"]}>
                  <img
                    src={SelfAssessmentScoreIcon}
                    alt="Self-assessment score icon"
                  />{" "}
                  {score.graph.assessmentScoreTitle}
                </h3>
                <p className={styles["description"]}>
                  {score.graph.assessmentScoreSubtitle}
                </p>
                <IndexScoreGraph
                  scoreChangeIconColor="blue"
                  view="compact"
                  dataPeriods={score.graph.periods}
                  periodLength={periodLength}
                  onChangePeriodLength={handleChangePeriodLength}
                  periodIndex={periodIndex}
                  onClickNextPeriod={handleClickNextPeriod}
                  onClickPreviousPeriod={handleClickPreviousPeriod}
                  displayValues="assessment"
                  assessmentColor="#1058e5"
                  biometricsColor="#a3d0e3"
                  moreLoading={shouldFetchMore && loading}
                />
              </div>
              <div
                className={clsx(
                  styles["chart-container"],
                  "mt-4 col-12  col-xxl-4"
                )}
              >
                <h3 className={styles["title"]}>
                  <img src={BiometricsIcon} alt="Biometrics icon" />{" "}
                  {score.graph.biometricsScoreTitle}
                </h3>
                <p className={styles["description"]}>
                  {score.graph.biometricsScoreSubtitle}
                </p>
                <IndexScoreGraph
                  scoreChangeIconColor="blue"
                  view="compact"
                  dataPeriods={score.graph.periods}
                  periodLength={periodLength}
                  onChangePeriodLength={handleChangePeriodLength}
                  periodIndex={periodIndex}
                  onClickNextPeriod={handleClickNextPeriod}
                  onClickPreviousPeriod={handleClickPreviousPeriod}
                  displayValues="biometrics"
                  assessmentColor="#1058e5"
                  biometricsColor="#a3d0e3"
                  moreLoading={shouldFetchMore && loading}
                />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const PillarDetails = () => {
  const { t } = useTranslation("index");
  const navigate = useNavigate();
  const location = useLocation();
  const { woneindexType } = useParams();

  const { learner } = useAuth();
  const {
    screens: {
      index: { version: screenVersion },
    },
  } = learner;

  const isFromHome = !location.state || location.state?.from?.includes("home");
  const backTo = isFromHome ? "/user/home" : "/user/woneindex";
  const navTitle = isFromHome
    ? t("index:navTitles.home")
    : t("index:navTitles.index");

  let PILLAR_QUERY;

  if (woneindexType === "energy") {
    PILLAR_QUERY = ENERGY_PILLAR_DETAIL_V2;
  } else if (woneindexType === "resilience") {
    PILLAR_QUERY = RESILIENCE_PILLAR_DETAIL_V2;
  } else if (woneindexType === "activity") {
    PILLAR_QUERY = ACTIVITY_PILLAR_DETAIL_V2;
  } else {
    navigate(backTo);
  }

  const { loading, error, data, fetchMore } = useQuery(PILLAR_QUERY, {
    fetchPolicy: "cache-and-network",
    onError(error) {
      logError("Error while fetching Wone Activity Data", error);
    },
    variables: {
      appInfo,
      first: 1,
      offset: 0,
    },
  });

  const screenQuery =
    screenVersion === "v1" ? LEARNER_SCREEN_INDEX_V1 : LEARNER_SCREEN_INDEX_V2;
  const [fetchScreenData, { data: screenData }] = useLazyQuery(screenQuery, {
    fetchPolicy: "cache-and-network",
    onError(error) {
      logError("Error while fetching wone index data", error);
    },
    variables: {
      appInfo,
      first: 1,
      offset: 0,
    },
  });

  useEffect(() => {
    if (data != null && screenData == null) {
      fetchScreenData();
    }
  }, [data, screenData, fetchScreenData]);

  if (loading && !data) {
    return (
      <PageContainer>
        <PageHeader showBackButton backTo={backTo} title={navTitle} />
        <PageContent maxWidth="lg">
          <PageLoading />
        </PageContent>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <PageHeader showBackButton backTo={backTo} title={navTitle} />
        <PageContent maxWidth="lg">
          <PageError />
        </PageContent>
      </PageContainer>
    );
  }

  const {
    learner: {
      screens: {
        index: { showBetaFlag, breakdown },
      },
    },
  } = data;

  if (!breakdown) {
    return <Navigate to={backTo} />;
  }

  const breakdownPillar = breakdown[woneindexType];

  return (
    <PageContainer>
      <PageHeader showBackButton title={navTitle} />

      <PageContent maxWidth="lg">
        {screenVersion === "v1" ? (
          <PillarDetailsContentV1 breakdownPillar={breakdownPillar} />
        ) : (
          <PillarDetailsContentV2
            breakdownPillar={breakdownPillar}
            showBetaFlag={showBetaFlag}
            fetchMore={fetchMore}
            loading={loading}
          />
        )}
      </PageContent>
    </PageContainer>
  );
};

export default PillarDetails;
